import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Activity, AlertCircle, AlertTriangle, Clock } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import {
  fetchDashboardStatus,
  fetchDashboardAlerts,
} from "../../../store/dashboardSlice";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

// Previous color constants remain the same
const COLORS = {
  expired: "#FF6B6B",
  dueToday: "#4ECDC4",
  dueSoon: "#FFD93D",
  upcoming: "#6C5CE7",
};

// Previous getStatusColor function remains the same
const getStatusColor = (status, index, totalStatuses) => {
  const baseHue = 220;
  const startSaturation = 80;
  const endSaturation = 60;
  const startLightness = 30;
  const endLightness = 70;

  const saturation =
    startSaturation +
    ((endSaturation - startSaturation) * index) / (totalStatuses - 1);
  const lightness =
    startLightness +
    ((endLightness - startLightness) * index) / (totalStatuses - 1);

  return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
};

// Updated StatusCard for better mobile display
const StatusCard = ({ title, count, onClick, isActive, color }) => {
  const navigate = useNavigate();
  
  const statusRoutes = {
    'PE': '/dpeindent',
    'L1': '/dlindent',
    'L2': '/dl2indent',
    'PD': '/dpdindent',
    'PH': '/dphindent',
    'SO': '/dsoindent',
    'SH': '/dshindent',
    'Closed': '/dclosed'
  };

  const handleClick = () => {
    const route = statusRoutes[title];
    if (route) {
      navigate(route);
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        backgroundColor: isActive ? color : color,
        opacity: isActive ? 1 : 0.85,
      }}
      className={`
        rounded-lg p-2 md:p-3 
        transform transition-all duration-300 
        ${
          isActive
            ? "scale-105 shadow-lg"
            : "shadow-md hover:scale-102 cursor-pointer"
        }
        flex justify-between items-center
      `}
    >
      <span className="font-semibold text-white text-xs md:text-sm">
        {title}
      </span>
      <span className="text-sm md:text-lg font-bold px-1.5 md:px-2 py-0.5 md:py-1 bg-white bg-opacity-20 rounded-full text-white">
        {count}
      </span>
    </div>
  );
};

// Updated AlertButton for better mobile display
const AlertButton = ({ title, counts, icon: Icon, color, path }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={() => navigate(path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderLeftColor: color,
        backgroundColor: isHovered ? `${color}11` : "white",
      }}
      className="rounded-lg shadow-md p-2 md:p-3 border-l-4 transform transition-all duration-300 hover:shadow-xl hover:scale-102 w-full"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Icon
              className="w-3 h-3 md:w-4 md:h-4 transition-transform duration-300"
              style={{
                color,
                transform: isHovered ? "scale(1.1)" : "scale(1)",
              }}
            />
            <span
              className="font-semibold text-xs md:text-sm"
              style={{ color: isHovered ? color : "#374151" }}
            >
              
              {title}
            </span>
          </div>
          <div
            className="px-1.5 md:px-2 py-0.5 rounded-full text-xs font-bold"
            style={{
              backgroundColor: `${color}22`,
              color: color,
            }}
          >
            {counts.total}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1 text-[10px] md:text-xs">
          <div className="bg-green-50 p-1 rounded text-center">
            <div className="text-green-700 font-medium">{counts.completed}</div>
            <div className="text-green-600">Done</div>
          </div>
          <div className="bg-yellow-50 p-1 rounded text-center">
            <div className="text-yellow-700 font-medium">{counts.partial}</div>
            <div className="text-yellow-600">Part</div>
          </div>
          <div className="bg-gray-50 p-1 rounded text-center">
            <div className="text-gray-700 font-medium">{counts.pending}</div>
            <div className="text-gray-600">Pend</div>
          </div>
        </div>
      </div>
    </button>
  );
};

const DashboardChart = ({ alerts, activeCategory, setActiveCategory }) => {
  const calculateItems = (items = []) => ({
    pending: items.reduce(
      (acc, item) =>
        acc + ((item.ReceiptStatus || "Pending") === "Pending" ? 1 : 0),
      0
    ),
    partial: items.reduce(
      (acc, item) =>
        acc + ((item.ReceiptStatus || "") === "Partially Received" ? 1 : 0),
      0
    ),
  });

  const data = [
    {
      name: "Expired",
      ...calculateItems(alerts.default),
      color: COLORS.expired,
    },
    {
      name: "Due Today",
      ...calculateItems(alerts.danger),
      color: COLORS.dueToday,
    },
    {
      name: "Due Soon",
      ...calculateItems(alerts.alert),
      color: COLORS.dueSoon,
    },
    {
      name: "Upcoming",
      ...calculateItems(alerts.warning),
      color: COLORS.upcoming,
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.[0]) {
      const { pending, partial, color } = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
          <p className="font-semibold text-gray-800">{label}</p>
          <p className="text-sm text-gray-600 mt-1">
            Pending:{" "}
            <span className="font-bold" style={{ color }}>
              {pending}
            </span>
          </p>
          <p className="text-sm text-gray-600">
            Partial:{" "}
            <span className="font-bold" style={{ color }}>
              {partial}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const Legend = () => (
  <div className="hidden md:flex flex-wrap gap-4 justify-center mt-4">
    {data.map((item, index) => (
      <div
        key={index}
        onClick={() => setActiveCategory(item.name)}
        className={`
          flex items-center gap-2 cursor-pointer p-2 rounded-lg
          transition-all duration-300
          ${
            activeCategory === item.name
              ? "bg-gray-100 scale-105"
              : "hover:bg-gray-50"
          }
        `}
      >
      </div>
    ))}
  </div>
);
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
            barSize={40}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280", fontSize: 12, fontWeight: 500 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280", fontSize: 12 }}
              width={40}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Bar
              dataKey="pending"
              stackId="a"
              name="Pending"
              radius={[8, 8, 0, 0]}
              animationDuration={1500}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  opacity={
                    activeCategory === null || activeCategory === entry.name
                      ? 1
                      : 0.3
                  }
                  className="transition-opacity duration-300"
                />
              ))}
            </Bar>
            <Bar
              dataKey="partial"
              stackId="a"
              name="Partial"
              radius={[8, 8, 0, 0]}
              animationDuration={1500}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  opacity={
                    activeCategory === null || activeCategory === entry.name
                      ? 0.6
                      : 0.2
                  }
                  className="transition-opacity duration-300"
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { role, id } = useSelector((state) => state.auth); // Add this line to get auth state
  const { 
    status, 
    alerts, 
    loading: { status: statusLoading, alerts: alertsLoading }, 
    error 
  } = useSelector((state) => state.dashboard);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (role && id) { // Only fetch if we have auth data
      console.log('Fetching dashboard data with role:', role, 'id:', id);
      dispatch(fetchDashboardStatus());
      dispatch(fetchDashboardAlerts());
    }
  }, [dispatch, role, id]);

  const getStatusCounts = (items = []) => ({
    completed: items.reduce(
      (acc, item) => acc + ((item.ReceiptStatus || "") === "Completed" ? 1 : 0),
      0
    ),
    partial: items.reduce(
      (acc, item) =>
        acc + ((item.ReceiptStatus || "") === "Partially Received" ? 1 : 0),
      0
    ),
    pending: items.reduce(
      (acc, item) =>
        acc + ((item.ReceiptStatus || "Pending") === "Pending" ? 1 : 0),
      0
    ),
    total: items.length,
  });

  // Loading states
  const isLoading = statusLoading || alertsLoading;
  if (!role || !id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user information...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => {
              dispatch(fetchDashboardStatus());
              dispatch(fetchDashboardAlerts());
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statusEntries = Object.entries(status || {});
  const totalStatuses = statusEntries.length;

  const alertButtons = [
    {
      title: "Expired",
      items: alerts?.default || [],
      icon: Clock,
      color: COLORS.expired,
      path: "/alerts/expired",
      counts: getStatusCounts(alerts?.default),
    },
    {
      title: "Due Today  (1 day left)",
      items: alerts?.danger || [],
      icon: AlertCircle,
      color: COLORS.dueToday,
      path: "/alerts/due-today",
      counts: getStatusCounts(alerts?.danger),
    },
    {
      title: "Due Soon  (2-3 days left)",
      items: alerts?.alert || [],
      icon: AlertTriangle,
      color: COLORS.dueSoon,
      path: "/alerts/due-soon",
      counts: getStatusCounts(alerts?.alert),
    },
    {
      title: "Upcoming  (4-7 days left)",
      items: alerts?.warning || [],
      icon: Activity,
      color: COLORS.upcoming,
      path: "/alerts/upcoming",
      counts: getStatusCounts(alerts?.warning),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header showExportButton={false} customFilename="export" />
      <main className="flex-1 p-2 md:p-4 mt-2 mb-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-2 md:space-y-4 pb-4">
          {/* Alert Buttons */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
            {alertButtons.map((button, index) => (
              <AlertButton key={index} {...button} />
            ))}
          </div>

          {/* Charts and Status Section */}
          <div className="space-y-2 md:space-y-4">
            {/* Alert Distribution Chart */}
            <div className="bg-white rounded-xl shadow-md p-2 md:p-4">
              <h2 className="text-sm md:text-lg font-bold mb-2 md:mb-3">
                Alert Distribution
              </h2>
              <div className="md:h">
                <DashboardChart
                  alerts={alerts || {}}
                  activeCategory={activeCategory}
                  setActiveCategory={setActiveCategory}
                />
              </div>
            </div>

            {/* Status Overview */}
            <div className="bg-white rounded-xl shadow-md p-2 md:p-4">
              <h2 className="text-sm md:text-lg font-bold mb-2 md:mb-3">
                Total IMS Status
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 md:gap-2">
                {statusEntries.map(([key, value], index) => (
                  <StatusCard
                    key={key}
                    title={key}
                    count={value || 0}
                    onClick={() => setActiveCategory(key)}
                    isActive={activeCategory === key}
                    color={getStatusColor(key, index, totalStatuses)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;